import { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { interviews } from "@/mocks/interviews";

export default function InterviewsSection() {
  const { t } = useTranslation();
  const [playingId, setPlayingId] = useState<string | null>(null);
  const [featured, ...rest] = interviews;

  return (
    <section className="py-16 bg-gray-950 relative overflow-hidden">
      <div className="absolute inset-0 opacity-10">
        <img
          src="https://public.readdy.ai/ai/img_res/0fe32bb374c36b8c4a7834e648d5a41d.jpg"
          alt=""
          className="w-full h-full object-cover"
        />
      </div>
      <div className="absolute inset-0 bg-gradient-to-b from-gray-950/80 via-gray-950/60 to-gray-950/90"></div>

      <div className="relative z-10 max-w-[1400px] mx-auto px-4 md:px-8">
        <div className="flex flex-wrap items-start sm:items-end justify-between mb-8 md:mb-10 gap-3 md:gap-4">
          <div>
            <div className="flex items-center gap-2 mb-3">
              <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse"></div>
              <span className="text-red-400 text-xs font-bold uppercase tracking-widest">{t("interviews.exclusive.badge")}</span>
            </div>
            <h2 className="text-2xl md:text-3xl font-bold text-white font-serif">{t("section.interviews.title")}</h2>
            <p className="text-gray-400 mt-1 text-sm">{t("interviews.subtitle.wazi")}</p>
          </div>
          <Link
            to="/interviews"
            className="flex-shrink-0 flex items-center gap-2 text-sm font-semibold text-orange-400 hover:text-orange-300 transition-colors whitespace-nowrap cursor-pointer"
          >
            {t("interviews.viewAll")} <i className="ri-arrow-right-line"></i>
          </Link>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          {/* Featured interview */}
          <div className="lg:col-span-3">
            <div className="relative rounded-2xl overflow-hidden bg-gray-900 border border-gray-800">
              {playingId === featured.id ? (
                <div className="aspect-video">
                  <iframe
                    src={`${featured.videoUrl}?autoplay=1`}
                    title={featured.title}
                    className="w-full h-full"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  ></iframe>
                </div>
              ) : (
                <div className="relative aspect-video cursor-pointer group" onClick={() => setPlayingId(featured.id)}>
                  <img src={featured.thumbnail} alt={featured.title} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" />
                  <div className="absolute inset-0 bg-black/40 group-hover:bg-black/30 transition-colors"></div>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-16 h-16 flex items-center justify-center rounded-full bg-orange-500 group-hover:bg-orange-400 group-hover:scale-110 transition-all duration-300">
                      <i className="ri-play-fill text-white text-3xl ml-1"></i>
                    </div>
                  </div>
                  <div className="absolute bottom-4 right-4 bg-black/70 text-white text-xs font-semibold px-2.5 py-1 rounded-full flex items-center gap-1">
                    <i className="ri-time-line"></i>{featured.duration}
                  </div>
                  <div className="absolute top-4 left-4 flex items-center gap-2">
                    <span className="bg-red-500 text-white text-xs font-bold px-3 py-1 rounded-full flex items-center gap-1">
                      <i className="ri-live-line"></i>{t("interviews.exclusive.badge")}
                    </span>
                  </div>
                </div>
              )}

              <div className="p-5">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-full overflow-hidden bg-gray-700 flex-shrink-0">
                    <img src={featured.guestPhoto} alt={featured.guest} className="w-full h-full object-cover object-top" />
                  </div>
                  <div>
                    <p className="text-white font-bold text-sm">{featured.guest}</p>
                    <p className="text-gray-400 text-xs">{featured.guestTitle}</p>
                  </div>
                  <div className="ml-auto flex items-center gap-1 text-gray-500 text-xs">
                    <i className="ri-eye-line"></i>
                    {(featured.views / 1000).toFixed(0)}K {t("interviews.views")}
                  </div>
                </div>
                <h3 className="text-white font-bold text-base leading-snug mb-2">{featured.title}</h3>
                <p className="text-gray-400 text-sm line-clamp-2">{featured.excerpt}</p>
                <div className="flex flex-wrap gap-2 mt-3">
                  {featured.tags.map((tag) => (
                    <span key={tag} className="text-xs bg-gray-800 text-gray-400 px-2.5 py-1 rounded-full">{tag}</span>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Side list */}
          <div className="lg:col-span-2 flex flex-col gap-4">
            {rest.slice(0, 4).map((interview) => (
              <div
                key={interview.id}
                className="group flex gap-4 bg-gray-900 rounded-2xl border border-gray-800 hover:border-orange-500/40 p-4 transition-all cursor-pointer"
                onClick={() => setPlayingId(interview.id)}
              >
                <div className="relative w-24 sm:w-28 flex-shrink-0 rounded-xl overflow-hidden" style={{ height: "72px" }}>
                  <img src={interview.thumbnail} alt={interview.title} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300" />
                  <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                    <div className="w-8 h-8 flex items-center justify-center rounded-full bg-orange-500/90 group-hover:bg-orange-400 transition-colors">
                      <i className="ri-play-fill text-white text-sm ml-0.5"></i>
                    </div>
                  </div>
                  <div className="absolute bottom-1 right-1 bg-black/70 text-white text-xs px-1.5 py-0.5 rounded font-medium">{interview.duration}</div>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xs text-orange-400 font-semibold">{interview.countryFlag} {interview.sector}</span>
                  </div>
                  <h4 className="text-white text-sm font-semibold leading-snug line-clamp-2 group-hover:text-orange-300 transition-colors mb-1">{interview.title}</h4>
                  <div className="flex items-center gap-2 text-xs text-gray-500">
                    <span>{interview.guest}</span>
                    <span>·</span>
                    <span className="flex items-center gap-1"><i className="ri-eye-line"></i>{(interview.views / 1000).toFixed(0)}K</span>
                  </div>
                </div>
              </div>
            ))}

            <Link
              to="/interviews"
              className="flex items-center justify-center gap-2 bg-gray-800 hover:bg-gray-700 border border-gray-700 text-gray-300 hover:text-white font-semibold text-sm px-5 py-3 rounded-2xl transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-play-circle-line text-orange-400"></i>
              {t("interviews.viewAllFull")}
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}
